import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, ShoppingCart, Menu, X, ChevronDown, Sun, Moon } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { useCurrency } from '../context/CurrencyContext';
import { useTheme } from '../context/ThemeContext';
import AuthModal from './AuthModal';
import UserMenu from './UserMenu';
import { BRANDS, COUNTRIES } from '../mock';
import BrandImage from './BrandImage';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from './ui/dropdown-menu';

const Logo = () => (
  <div className="relative w-9 h-9">
    <div className="absolute inset-0 rounded-lg" style={{
      background: 'linear-gradient(135deg, #8247E5, #A855F7)',
      boxShadow: '0 4px 14px rgba(130,71,229,0.35)'
    }} />
    <div className="absolute inset-0 flex items-center justify-center">
      <svg viewBox="0 0 40 40" className="w-5 h-5" fill="none">
        <path d="M12 8 L20 4 L28 8 L28 16 L20 20 L12 16 Z" fill="white" opacity="0.95" />
        <path d="M12 24 L20 20 L28 24 L28 32 L20 36 L12 32 Z" fill="white" opacity="0.7" />
      </svg>
    </div>
  </div>
);

const Header = () => {
  const { count } = useCart();
  const { user } = useAuth();
  const { countryCode, setCountry, currency } = useCurrency();
  const { theme, toggle: toggleTheme, isDark } = useTheme();
  const [query, setQuery] = useState('');
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const [authMode, setAuthMode] = useState('login');
  const navigate = useNavigate();

  const country = COUNTRIES.find(c => c.code === countryCode) || COUNTRIES[0];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const suggestions = query.length > 0
    ? BRANDS.filter(b => b.name.toLowerCase().includes(query.toLowerCase())).slice(0, 6)
    : [];

  const handleSelect = (id) => { setQuery(''); navigate(`/product/${id}`); };

  return (
    <header className={`sticky top-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white/85 dark:bg-neutral-950/85 backdrop-blur-xl border-b border-neutral-200 dark:border-neutral-800 shadow-sm' : 'bg-white/60 dark:bg-neutral-950/60 backdrop-blur-md border-b border-transparent'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 gap-4">
          <Link to="/" className="flex items-center gap-2 shrink-0 group">
            <Logo />
            <span className="font-display font-bold text-xl text-neutral-900">TokenGate</span>
          </Link>

          <div className="hidden md:flex flex-1 max-w-2xl relative">
            <div className="relative w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
              <Input value={query} onChange={(e) => setQuery(e.target.value)}
                placeholder="Search for brands, gift cards, mobile top-ups..."
                className="pl-10 pr-4 h-11 bg-neutral-50 border-neutral-200 focus-visible:ring-[#8247E5]" />
              {suggestions.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-neutral-200 rounded-xl shadow-xl overflow-hidden">
                  {suggestions.map(s => (
                    <button key={s.id} onClick={() => handleSelect(s.id)}
                      className="w-full flex items-center gap-3 px-4 py-3 hover:bg-violet-50 text-left transition-colors">
                      <BrandImage brand={s} className="w-8 h-8 rounded object-contain bg-neutral-100 p-1" tileClassName="w-8 h-8 rounded text-[10px]" />
                      <div>
                        <div className="text-sm font-medium text-neutral-900">{s.name}</div>
                        <div className="text-xs text-neutral-500 capitalize">{s.category}</div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <Button
              data-testid="theme-toggle"
              onClick={toggleTheme}
              variant="ghost"
              size="icon"
              className="relative h-9 w-9 rounded-full text-neutral-700 dark:text-neutral-200 hover:bg-neutral-100 dark:hover:bg-neutral-800"
              aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
              title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              <Sun className={`h-4 w-4 transition-all ${isDark ? 'scale-0 rotate-90 opacity-0' : 'scale-100 rotate-0 opacity-100'}`} />
              <Moon className={`absolute h-4 w-4 transition-all ${isDark ? 'scale-100 rotate-0 opacity-100' : 'scale-0 -rotate-90 opacity-0'}`} />
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button data-testid="country-selector" variant="ghost" className="hidden md:flex gap-1 text-neutral-700 hover:text-neutral-900">
                  <span className="text-base">{country.flag}</span>
                  <span className="text-sm">{country.code}</span>
                  <span className="text-[10px] text-neutral-500 font-semibold">· {currency.symbol}{currency.code}</span>
                  <ChevronDown className="w-3.5 h-3.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64">
                <div className="px-2 py-1.5 text-[10px] text-neutral-500 font-semibold uppercase tracking-wider">Country &amp; currency</div>
                {COUNTRIES.map(c => (
                  <DropdownMenuItem key={c.code} data-testid={`country-option-${c.code}`} onClick={() => setCountry(c.code)} className="gap-2 justify-between">
                    <span className="flex items-center gap-2"><span>{c.flag}</span><span>{c.name}</span></span>
                    <span className="text-[10px] font-mono text-neutral-500">{(({US:'USD',GB:'GBP',DE:'EUR',FR:'EUR',ES:'EUR',IN:'INR',BR:'BRL',JP:'JPY',CA:'CAD',AU:'AUD'})[c.code]) || 'USD'}</span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <Link to="/checkout" className="relative">
              <Button variant="ghost" size="icon" className="relative">
                <ShoppingCart className="w-5 h-5" />
                {count > 0 && (
                  <Badge className="absolute -top-1 -right-1 h-5 min-w-5 px-1 bg-[#8247E5] hover:bg-[#8247E5] text-white text-[10px] font-bold">
                    {count}
                  </Badge>
                )}
              </Button>
            </Link>

            {user ? (
              <div className="hidden md:block"><UserMenu /></div>
            ) : (
              <Button onClick={() => { setAuthMode('login'); setAuthOpen(true); }} className="hidden md:inline-flex bg-neutral-900 hover:bg-neutral-800 text-white h-10 px-4">
                Sign in
              </Button>
            )}

            <Button variant="ghost" size="icon" className="md:hidden" onClick={() => setMobileOpen(!mobileOpen)}>
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
          </div>
        </div>

        <nav className="hidden md:flex items-center gap-1 h-12 overflow-x-auto no-scrollbar text-sm">
          <Link to="/swap" className="px-3 py-1.5 rounded-md transition-colors flex items-center gap-1.5 font-semibold text-[#8247E5] hover:bg-violet-50">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#8247E5] pulse-glow" />
            Swap
          </Link>
          <div className="w-px h-4 bg-neutral-200 mx-1" />
          {[['shopping','Shopping'], ['food','Food'], ['gaming','Gaming'], ['entertainment','Entertainment'], ['travel','Travel'], ['apparel','Apparel']].map(([k,l]) => (
            <Link key={k} to={`/browse/${k}`} className="px-3 py-1.5 text-neutral-700 hover:text-[#8247E5] hover:bg-violet-50 rounded-md transition-colors">{l}</Link>
          ))}
          <Link to="/mobile-topup" className="px-3 py-1.5 text-neutral-700 hover:text-[#8247E5] hover:bg-violet-50 rounded-md transition-colors">Mobile Refill</Link>
          <Link to="/esim" className="px-3 py-1.5 text-neutral-700 hover:text-[#8247E5] hover:bg-violet-50 rounded-md transition-colors">eSIM</Link>
          <Link to="/browse/crypto" className="px-3 py-1.5 text-neutral-700 hover:text-[#8247E5] hover:bg-violet-50 rounded-md transition-colors">Crypto Services</Link>
        </nav>

        {mobileOpen && (
          <div className="md:hidden pb-4 space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
              <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search brands..." className="pl-10 h-11 bg-neutral-50" />
            </div>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <Link to="/swap" onClick={() => setMobileOpen(false)} className="col-span-2 px-3 py-2 bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white rounded-lg font-semibold flex items-center justify-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-white pulse-glow" /> Swap tokens
              </Link>
              {['shopping','food','gaming','entertainment','travel','apparel'].map(c => (
                <Link key={c} to={`/browse/${c}`} onClick={() => setMobileOpen(false)}
                  className="px-3 py-2 bg-neutral-50 rounded-lg capitalize">{c}</Link>
              ))}
              <Link to="/mobile-topup" onClick={() => setMobileOpen(false)} className="px-3 py-2 bg-neutral-50 rounded-lg">Mobile Refill</Link>
              <Link to="/esim" onClick={() => setMobileOpen(false)} className="px-3 py-2 bg-neutral-50 rounded-lg">eSIM</Link>
            </div>
            <Button onClick={() => { setAuthMode('login'); setAuthOpen(true); setMobileOpen(false); }} className="w-full bg-neutral-900 hover:bg-neutral-800">Sign in</Button>
            <Button onClick={() => { setAuthMode('signup'); setAuthOpen(true); setMobileOpen(false); }} variant="outline" className="w-full">Create account</Button>
          </div>
        )}
      </div>
      <AuthModal open={authOpen} onOpenChange={setAuthOpen} initialMode={authMode} />
    </header>
  );
};

export default Header;
